import { useState, useRef, useEffect } from "react"
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    IconButton,
    Typography,
    Autocomplete,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import api from "../../api/api.js"
import { LoadingButton } from "@mui/lab"
import en_ja from "../../translations/en_ja.js"

const DEFAULT_COORDS = { lat: 35.6762, lng: 139.6503 }

const AddClinic = ({
    open,
    onClose,
    editMode = false,
    clinicData = null,
    onSubmit,
    setSnackbarOpen,
    setSnackbarMessage,
    originalClinicData,
    setSnackbarType,
}) => {
    const [loading, setLoading] = useState(false)
    const user = JSON.parse(localStorage.getItem("user") || "{}")

    const [predictions, setPredictions] = useState([])
    const [mapLoaded, setMapLoaded] = useState(false)
    const [mapError, setMapError] = useState("")
    const mapRef = useRef(null)
    const mapInstance = useRef(null)
    const markerInstance = useRef(null)
    const autocompleteService = useRef(null)
    const placesService = useRef(null)
    const geocoder = useRef(null)
    const [initialFormData, setInitialFormData] = useState(null)

    const [errors, setErrors] = useState({})
    const [formData, setFormData] = useState({
        clinicName: "",
        email: "",
        phoneNo: "",
        clinicAddress: "",
        landlineNo: "",
        about: "",
        coordinates: DEFAULT_COORDS,
    })

    const [selectedPlaceId, setSelectedPlaceId] = useState(null)

    const handleSelectPrediction = (placeId, description) => {
        setSelectedPlaceId(placeId)
        setFormData((prev) => ({ ...prev, clinicAddress: description }))
        setPredictions([])
        if (!placesService.current) return

        placesService.current.getDetails({ placeId }, (place, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && place.geometry && place.geometry.location) {
                const location = place.geometry.location
                setFormData((prev) => ({
                    ...prev,
                    coordinates: { lat: location.lat(), lng: location.lng() },
                    clinicAddress: place.formatted_address,
                }))
            }
        })
    }

    const loadGoogleMaps = () => {
        return new Promise((resolve, reject) => {
            // Check if Google Maps is already loaded and ready
            if (window.google && window.google.maps && window.google.maps.Map) {
                setMapLoaded(true)
                resolve()
                return
            }

            // Check if script is already loading
            const existingScript = document.querySelector('script[src*="maps.googleapis.com"]')
            if (existingScript) {
                // Script exists, wait for it to load
                const checkGoogleMapsReady = () => {
                    if (window.google && window.google.maps && window.google.maps.Map) {
                        setMapLoaded(true)
                        resolve()
                    } else {
                        setTimeout(checkGoogleMapsReady, 100)
                    }
                }
                checkGoogleMapsReady()
                return
            }

            // Create and load the script
            const script = document.createElement("script")
            script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyD-206GuB2zh4Pl6c2EEf4c1U0Qw-ju9kY&libraries=places&callback=initGoogleMaps`
            script.async = true
            script.defer = true

            // Create global callback
            window.initGoogleMaps = () => {
                if (window.google && window.google.maps && window.google.maps.Map) {
                    setMapLoaded(true)
                    resolve()
                } else {
                    setMapError(en_ja["Google Maps failed to initialize properly"])
                    reject(new Error(en_ja["Google Maps failed to initialize"]))
                }
            }

            script.onerror = () => {
                setMapError(en_ja["Failed to load Google Maps script"]) // <- keep as-is
                reject(new Error(en_ja["Failed to load Google Maps script"]))
            }

            document.head.appendChild(script)
        })
    }

    const initializeMap = (centerCoords = formData.coordinates) => {
        if (!window.google || !window.google.maps || !window.google.maps.Map || !mapRef.current) {
            console.log("Google Maps not ready or map container not available", centerCoords)
            return
        }

        try {
            // If map already exists, just update position
            if (mapInstance.current && markerInstance.current) {
                const latlng = new window.google.maps.LatLng(centerCoords.lat, centerCoords.lng)
                mapInstance.current.setCenter(latlng)
                markerInstance.current.setPosition(latlng)
                return
            }

            // Create new map instance
            const centerLatLng = new window.google.maps.LatLng(centerCoords.lat, centerCoords.lng)
            const map = new window.google.maps.Map(mapRef.current, {
                center: centerLatLng,
                zoom: 13,
            })

            const marker = new window.google.maps.Marker({
                position: centerLatLng,
                map,
                draggable: true,
            })

            // Add click listener
            map.addListener("click", (e) => {
                if (e.latLng) {
                    const lat = e.latLng.lat()
                    const lng = e.latLng.lng()
                    marker.setPosition(e.latLng)
                    setFormData((prev) => ({ ...prev, coordinates: { lat, lng } }))
                    setSelectedPlaceId(null)

                    // Reverse geocode
                    if (geocoder.current) {
                        geocoder.current.geocode({ location: { lat, lng } }, (results, status) => {
                            if (status === "OK" && results && results[0]) {
                                setFormData((prev) => ({ ...prev, clinicAddress: results[0].formatted_address }))
                            }
                        })
                    }
                }
            })

            // Add drag listener
            marker.addListener("dragend", (e) => {
                if (e.latLng) {
                    const lat = e.latLng.lat()
                    const lng = e.latLng.lng()
                    setFormData((prev) => ({ ...prev, coordinates: { lat, lng } }))
                    setSelectedPlaceId(null)

                    // Reverse geocode
                    if (geocoder.current) {
                        geocoder.current.geocode({ location: { lat, lng } }, (results, status) => {
                            if (status === "OK" && results && results[0]) {
                                setFormData((prev) => ({ ...prev, clinicAddress: results[0].formatted_address }))
                            }
                        })
                    }
                }
            })

            // Store references
            mapInstance.current = map
            markerInstance.current = marker

            // Initialize services
            if (window.google.maps.places) {
                autocompleteService.current = new window.google.maps.places.AutocompleteService()
                placesService.current = new window.google.maps.places.PlacesService(map)
            }
            geocoder.current = new window.google.maps.Geocoder()

            // Trigger resize after a short delay to ensure proper rendering
            setTimeout(() => {
                if (mapInstance.current) {
                    window.google.maps.event.trigger(mapInstance.current, "resize")
                    mapInstance.current.setCenter(centerLatLng)
                }
            }, 300)

            console.log("Map initialized successfully")
        } catch (error) {
            console.error("Error initializing map:", error)
            setMapError(en_ja["Failed to initialize map: "] + error.message)
        }
    }

    useEffect(() => {
        if (!open) {
            // Clean up when dialog closes
            if (markerInstance.current) {
                markerInstance.current.setMap(null)
                markerInstance.current = null
            }
            if (mapInstance.current) {
                mapInstance.current = null
            }
            setMapLoaded(false)
            setMapError("")
            return
        }

        const initialCoords =
            editMode && clinicData && clinicData.latitude && clinicData.longitude
                ? { lat: Number(clinicData.latitude), lng: Number(clinicData.longitude) }
                : formData.coordinates || DEFAULT_COORDS

        if (editMode && clinicData) {
            const initialValues = {
                clinicName: clinicData.clinic_name || "",
                email: clinicData.email || "",
                phoneNo: clinicData.phone_no || "",
                clinicAddress: clinicData.clinic_location || "",
                landlineNo: clinicData.landline_no || "",
                about: clinicData.about || "",
                coordinates: initialCoords,
            }

            setFormData(initialValues)
            setInitialFormData(initialValues)
            setSelectedPlaceId(null)
        }

        // Load Google Maps with proper timing
        const initializeGoogleMaps = async () => {
            try {
                await loadGoogleMaps()
                // Wait for DOM to be ready and then initialize map
                setTimeout(() => {
                    if (mapRef.current) {
                        initializeMap(initialCoords)
                    }
                }, 500)
            } catch (error) {
                console.error("Failed to load Google Maps:", error)
                setMapError(en_ja["Failed to load Google Maps. Please check your internet connection."])
            }
        }

        // Small delay to ensure dialog is fully rendered
        const timeoutId = setTimeout(initializeGoogleMaps, 100)

        return () => {
            clearTimeout(timeoutId)
        }
    }, [open, editMode, clinicData])

    useEffect(() => {
        if (!mapInstance.current || !markerInstance.current || !formData.coordinates) return

        try {
            const { lat, lng } = formData.coordinates
            if (typeof lat === "number" && typeof lng === "number" && window.google && window.google.maps) {
                const latlng = new window.google.maps.LatLng(lat, lng)
                markerInstance.current.setPosition(latlng)
                mapInstance.current.setCenter(latlng)
            }
        } catch (error) {
            console.error("Error updating map position:", error)
        }
    }, [formData.coordinates])

    const validateForm = () => {
        const newErrors = {}
        if (!formData.clinicName.trim()) newErrors.clinicName = en_ja["Clinic name is required"]
        if (!formData.clinicAddress.trim()) newErrors.clinicAddress = en_ja["Clinic address is required"]
        if (!formData.email.trim()) newErrors.email = en_ja["Email is required"]
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = en_ja["Enter a valid email address"]
        const numberWithHyphenRegex = /^[\d-]+$/
        if (!formData.phoneNo.trim()) newErrors.phoneNo = en_ja["Phone number is required"]
        else if (!numberWithHyphenRegex.test(formData.phoneNo)) newErrors.phoneNo = en_ja["Enter only numbers"]
        if (!formData.landlineNo.trim()) newErrors.landlineNo = "Landline number is required";
        else if (!numberWithHyphenRegex.test(formData.landlineNo)) newErrors.landlineNo = "Enter only numbers";
        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleInputChange = (field) => (event) => {
        setFormData((prev) => ({ ...prev, [field]: event.target.value }))
        setErrors((prev) => ({ ...prev, [field]: "" }))
        if (field === "clinicAddress") setSelectedPlaceId(null) // reset placeId on manual typing
    }

    const handleSubmit = async () => {
        if (!validateForm()) return

        // Use Google Geocoder to check if address is valid
        if (!formData.clinicAddress.trim()) {
            setErrors((prev) => ({ ...prev, clinicAddress: en_ja["Clinic address is required"] }))
            return
        }

        if (!geocoder.current) return
        geocoder.current.geocode({ address: formData.clinicAddress }, async (results, status) => {
            if (status !== "OK" || !results || results.length === 0) {
                setErrors((prev) => ({ ...prev, clinicAddress: en_ja["Please enter a valid address"] }))
                return
            }

            // Update coordinates with geocoded result
            const location = results[0].geometry.location
            const coords = { lat: location.lat(), lng: location.lng() }

            setFormData((prev) => ({ ...prev, coordinates: coords }))

            // Continue with submission
            setLoading(true)
            const obj = {
                clinic_name: formData.clinicName,
                clinic_location: formData.clinicAddress,
                phone_no: formData.phoneNo,
                landline_no: formData.landlineNo,
                email: formData.email,
                longitude: coords.lng,
                latitude: coords.lat,
                userId: user?.id,
                about: formData?.about
            }

            try {
                let response
                if (editMode) response = await api.put(`v1/admin/edit/${clinicData?.id}`, obj)
                else response = await api.post("/v1/admin/register-clinic", obj)

                if (response.status === 200 || response.status === 201) {
                    setSnackbarMessage(
                        response?.data?.message || (editMode ? en_ja["Clinic updated successfully"] : en_ja["Clinic added successfully"]),
                    )
                    setSnackbarOpen(true)
                    setSnackbarType("success")
                    if (onSubmit) onSubmit()
                    handleClose()
                }
            } catch (error) {
                setSnackbarMessage(error?.response?.data?.error || error?.response?.data?.message || en_ja["Something went wrong."])
                setSnackbarOpen(true)
                setSnackbarType("error")
            } finally {
                setLoading(false)
            }
        })
    }

    const hasChanges = editMode && initialFormData
        ? JSON.stringify(formData) !== JSON.stringify(initialFormData)
        : true

    const handleClose = () => {
        setFormData({
            clinicName: "",
            email: "",
            phoneNo: "",
            clinicAddress: "",
            landlineNo: "",
            about: "",
            coordinates: DEFAULT_COORDS,
        })
        setSelectedPlaceId(null)
        setErrors({})
        onClose()
    }

    if (!open) return null

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">{editMode ? en_ja["Edit Clinic"] : en_ja["Add New Clinic"]}</Typography>
                    <IconButton onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>

                <Box sx={{ display: "flex", gap: 3 }}>
                    <TextField
                        fullWidth
                        label={en_ja["Clinic Name"]}
                        value={formData.clinicName}
                        onChange={handleInputChange("clinicName")}
                        margin="normal"
                        disabled={loading}
                        error={!!errors.clinicName}
                        helperText={errors.clinicName}
                    />

                    <TextField
                        fullWidth
                        label={en_ja["Email Address"]}
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange("email")}
                        margin="normal"
                        disabled={loading || editMode}
                        error={!!errors.email}
                        helperText={errors.email}
                    />
                </Box>

                <Box sx={{ display: "flex", gap: 3 }}>
                    <TextField
                        fullWidth
                        label={en_ja["Phone Number"]}
                        value={formData.phoneNo}
                        onChange={handleInputChange("phoneNo")}
                        margin="normal"
                        disabled={loading}
                        error={!!errors.phoneNo}
                        helperText={errors.phoneNo}
                    />
                    <TextField
                        fullWidth
                        label={en_ja["Landline Number"]}
                        value={formData.landlineNo}
                        onChange={handleInputChange("landlineNo")}
                        margin="normal"
                        disabled={loading}
                        error={!!errors.landlineNo}
                        helperText={errors.landlineNo}
                    />
                </Box>

                <Box sx={{ display: "flex", gap: 3 }}>
                    <Autocomplete
                        sx={{ flex: 1 }}
                        freeSolo
                        options={predictions.map((p) => ({ label: p.description, placeId: p.place_id }))}
                        value={formData.clinicAddress || ""}
                        onInputChange={(event, value) => {
                            setFormData((prev) => ({ ...prev, clinicAddress: value }))
                            setErrors((prev) => ({ ...prev, clinicAddress: "" }))
                            setSelectedPlaceId(null)
                            if (!value || !autocompleteService.current) {
                                setPredictions([])
                                return
                            }
                            autocompleteService.current.getPlacePredictions({ input: value }, (results) => {
                                setPredictions(results || [])
                            })
                        }}
                        onChange={(event, newValue) => {
                            if (typeof newValue === "string") {
                                setFormData((prev) => ({ ...prev, clinicAddress: newValue }))
                                setSelectedPlaceId(null)
                                setErrors((prev) => ({ ...prev, clinicAddress: "" }))
                            } else if (newValue && newValue.placeId) {
                                handleSelectPrediction(newValue.placeId, newValue.label)
                            } else {
                                setFormData((prev) => ({ ...prev, clinicAddress: "" }))
                                setSelectedPlaceId(null)
                            }
                            setPredictions([])
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={en_ja["Clinic Address"]}
                                error={!!errors.clinicAddress}
                                helperText={errors.clinicAddress}
                                margin="normal"
                                multiline
                                rows={2}
                                fullWidth
                            />
                        )}
                    />

                    <TextField
                        sx={{ flex: 1 }}
                        fullWidth
                        label={en_ja["About Clinic"]}
                        value={formData.about}
                        onChange={handleInputChange("about")}
                        margin="normal"
                        disabled={loading}
                        multiline
                        rows={2}
                    />
                </Box>

                <Box sx={{ flex: 1, position: "relative" }}>
                    <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
                        <Typography variant="body2" color="text.secondary">
                            {en_ja["Click on the map or drag the marker to set the clinic location"]}
                        </Typography>
                    </Box>

                    <Box sx={{ height: 350, position: "relative" }}>
                        {mapError ? (
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    height: "100%",
                                    flexDirection: "column",
                                }}
                            >
                                <Typography color="error" gutterBottom>
                                    {mapError}
                                </Typography>
                                <Button
                                    onClick={() => {
                                        setMapError("")
                                        loadGoogleMaps().then(() => initializeMap(formData.coordinates))
                                    }}
                                >
                                    {en_ja["Retry"]}
                                </Button>
                            </Box>
                        ) : !mapLoaded ? (
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                                <Typography>{en_ja["Loading Google Maps..."]}</Typography>
                            </Box>
                        ) : (
                            <div ref={mapRef} style={{ width: "100%", height: "100%" }} />
                        )}

                        <Box
                            sx={{
                                position: "absolute",
                                bottom: 16,
                                right: 16,
                                bgcolor: "background.paper",
                                p: 1,
                                borderRadius: 1,
                                boxShadow: 1,
                                fontSize: "0.75rem",
                            }}
                        >
                            <strong>{en_ja["Coordinates"]}:</strong>
                            <br />
                            {formData.coordinates.lat.toFixed(6)}, {formData.coordinates.lng.toFixed(6)}
                        </Box>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 3 }}>
                <Button onClick={handleClose} variant="outlined">
                    {en_ja["Cancel"]}
                </Button>

                <LoadingButton
                    onClick={handleSubmit}
                    loading={loading}
                    variant="contained"
                    disabled={loading || (editMode && !hasChanges)}
                    sx={{ backgroundColor: "rgba(220, 53, 69, 1)" }}
                >
                    {editMode ? en_ja["Update Clinic"] : en_ja["Add Clinic"]}
                </LoadingButton>

            </DialogActions>
        </Dialog>
    )
}

export default AddClinic
